<script setup>
import { onBeforeUnmount, onMounted, ref, watch } from "vue";
import Quill from "quill";
import "quill/dist/quill.snow.css";

const TOOLBAR = [
  ["bold", "italic", "underline", "strike"],
  [{ list: "ordered" }, { list: "bullet" }],
  ["link", "blockquote"],
  [{ direction: "rtl" }],
  ["clean"],
];

const FORMATS = [
  "bold",
  "italic",
  "underline",
  "strike",
  "link",
  "list",
  "blockquote",
  "direction",
];

const props = defineProps({
  modelValue: { type: String, default: "" },
  placeholder: { type: String, default: "" },
  disabled: { type: Boolean, default: false },
  minHeight: { type: String, default: "120px" },
});

const emit = defineEmits(["update:modelValue"]);

const container = ref(null);
let quill = null;

const currentHtml = () => (quill.getText().trim() ? quill.root.innerHTML : "");

const applyModelValue = (value) => {
  if (value === currentHtml()) return;
  if (value) {
    quill.clipboard.dangerouslyPasteHTML(value, "silent");
  } else {
    quill.setText("", "silent");
  }
  const normalized = currentHtml();
  if (normalized !== value) emit("update:modelValue", normalized);
};

onMounted(() => {
  quill = new Quill(container.value, {
    theme: "snow",
    placeholder: props.placeholder,
    formats: FORMATS,
    modules: { toolbar: TOOLBAR },
  });
  quill.enable(!props.disabled);
  if (props.modelValue) applyModelValue(props.modelValue);
  quill.on("text-change", (_delta, _oldDelta, source) => {
    if (source === "user") emit("update:modelValue", currentHtml());
  });
});

onBeforeUnmount(() => {
  quill = null;
});

watch(
  () => props.modelValue,
  (value) => {
    if (quill) applyModelValue(value);
  }
);

watch(
  () => props.disabled,
  (disabled) => {
    if (quill) quill.enable(!disabled);
  }
);
</script>

<template>
  <div class="rich-text-editor" :style="{ '--editor-min-height': minHeight }">
    <div ref="container"></div>
  </div>
</template>

<style scoped>
.rich-text-editor {
  min-width: 0;
}

.rich-text-editor :deep(.ql-toolbar.ql-snow) {
  border-radius: 0.5rem 0.5rem 0 0;
  padding: 4px 8px;
}

.rich-text-editor :deep(.ql-container.ql-snow) {
  border-radius: 0 0 0.5rem 0.5rem;
  font-family: inherit;
  font-size: 0.875rem;
}

.rich-text-editor :deep(.ql-editor) {
  min-height: var(--editor-min-height);
  max-height: 40vh;
  overflow-y: auto;
  text-align: start;
}
</style>
